import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LandingPageNew from './pages/LandingPageNew';
import Contact from './pages/Contact';
import Legal from './pages/Legal';
import CookieBanner from './components/CookieBanner';
import Demos from './pages/Demos';
import Demo from './pages/Demo';
import Demo2 from './pages/Demo2';
import { usePageTitle } from './hooks/usePageTitle';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from 'react-hot-toast'

// QR Management System Components
import { useAuth } from './AuthProvider';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import DashboardOverview from './pages/DashboardOverview';
import { Links } from './components/qr/Links';
import Stats from './pages/Stats';
import Books from './pages/Books';
import Campaigns from './pages/Campaigns';
import Content from './pages/Content';
import Reports from './pages/Reports';
import Landing from './pages/Landing';
import { Profile } from './components/qr/Profile';
import Listen from './pages/Listen';
import { AuthConfirm } from './components/AuthConfirm';
import { AuthRedirect } from './components/AuthRedirect';
import { TeamManagement } from './components/team/TeamManagement';
import { DebugStripe } from './components/qr/DebugStripe';
import { TeamInviteAccept } from './components/team/TeamInviteAccept';
import MultiShopPage from './pages/MultiShopPage';
import AuthorProfilePage from './pages/AuthorProfilePage';
import BookSamplePage from './pages/BookSamplePage';
import SpotifyPage from './pages/SpotifyPage';
import PricingAuth from './pages/PricingAuth';
import AIIdeas from './pages/AIIdeas';
import { useTrial } from './hooks/useTrial';
import { HelpSidebarLayout } from './components/help/HelpSidebar';

// Demo Pages
import SpotifyDemo from './pages/demos/SpotifyDemo';
import ShopDemo from './pages/demos/ShopDemo';
import AuthorDemo from './pages/demos/AuthorDemo';
import SampleDemo from './pages/demos/SampleDemo';

// QR Management Protected Route
function Protected({children}:{children:React.ReactElement}) {
  const {user,loading}=useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><p>Lade…</p></div>;
  return user ? children : <Navigate to="/qr/login"/>;
}

// Protected Route with Trial Check
function ProtectedWithTrial({children}:{children:React.ReactElement}) {
  const {user,loading:authLoading}=useAuth();
  const { trialStatus, loading: trialLoading, needsPayment } = useTrial();

  if (authLoading || trialLoading) {
    return <div className="flex items-center justify-center min-h-screen"><p>Lade…</p></div>;
  }

  if (!user) {
    return <Navigate to="/qr/login"/>;
  }

  // No longer redirect - allow access to dashboard with restricted features
  // The individual components will handle showing upgrade modals
  return children;
}

// QR Management Home Route
function QRHomePage() {
  const {user,loading}=useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><p>Lade…</p></div>;
  return user ? <Navigate to="/qr/dash"/> : <Navigate to="/qr/login"/>;
}

// QR Management Layout with Sidebar
function QRLayout({children}:{children:React.ReactElement}) {
  return (
    <HelpSidebarLayout>
      <Sidebar />
      <div className="flex-1 ml-64">
        {children}
      </div>
    </HelpSidebarLayout>
  );
}

// App Content Component (to use hooks inside Router context)
function AppContent() {
  // Use the page title hook to dynamically update titles
  usePageTitle();

  return (
    <AuthRedirect>
      <Routes>
        {/* Original Landing Page Routes */}
        <Route path="/" element={
          <div className="flex flex-col min-h-screen">
            <main className="flex-grow">
              <LandingPageNew />
            </main>
            <CookieBanner />
          </div>
        } />

        <Route path="/kontakt" element={
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <Contact />
            </main>
            <Footer />
            <CookieBanner />
          </div>
        } />
        <Route path="/impressum" element={
          <div className="flex flex-col min-h-screen">
            <main className="flex-grow">
              <Legal />
            </main>
            <Footer />
            <CookieBanner />
          </div>
        } />
        <Route path="/demos" element={
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <Demos />
            </main>
            <Footer />
            <CookieBanner />
          </div>
        } />
        <Route path="/demo" element={
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <Demo />
            </main>
            <Footer />
            <CookieBanner />
          </div>
        } />
        <Route path="/demo2" element={
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <Demo2 />
            </main>
            <Footer />
            <CookieBanner />
          </div>
        } />
        
        {/* Legal Pages */}
        <Route path="/privacy" element={
          <div className="flex flex-col min-h-screen">
            <main className="flex-grow">
              <PrivacyPolicy />
            </main>
            <Footer />
            <CookieBanner />
          </div>
        } />
        <Route path="/terms" element={
          <div className="flex flex-col min-h-screen">
            <main className="flex-grow">
              <TermsOfService />
            </main>
            <Footer />
            <CookieBanner />
          </div>
        } />
        
        {/* QR Management System Routes */}
        <Route path="/qr" element={<QRHomePage/>}/>
        <Route path="/qr/login" element={<Login/>}/>
        <Route path="/auth/confirm" element={<AuthConfirm/>}/>
        <Route path="/qr/l/:slug" element={<Landing/>}/>
        <Route path="/listen/:slug" element={<Listen/>}/>
        <Route path="/listen/:accountSlug/:slug" element={<Listen/>}/>
        {/* Redirect routes removed - handled by backend redirect.ts */}
        
        {/* Demo Routes */}
        <Route path="/qr/demo-spotify" element={<SpotifyDemo />} />
        <Route path="/qr/demo-shop" element={<ShopDemo />} />
        <Route path="/qr/demo-author" element={<AuthorDemo />} />
        <Route path="/qr/demo-sample" element={<SampleDemo />} />
        
        <Route path="/qr/dash" element={<Protected><QRLayout><DashboardOverview/></QRLayout></Protected>} />
        <Route path="/qr/dash/links" element={<ProtectedWithTrial><QRLayout><Links/></QRLayout></ProtectedWithTrial>} />
        <Route path="/qr/dash/content" element={<ProtectedWithTrial><QRLayout><Content/></QRLayout></ProtectedWithTrial>} />
        <Route path="/qr/dash/stats" element={<ProtectedWithTrial><QRLayout><Stats/></QRLayout></ProtectedWithTrial>} />
        <Route path="/qr/dash/reports" element={<ProtectedWithTrial><QRLayout><Reports/></QRLayout></ProtectedWithTrial>} />
        <Route path="/qr/dash/profile" element={<Protected><QRLayout><Profile/></QRLayout></Protected>} />
        <Route path="/qr/dash/team" element={<ProtectedWithTrial><QRLayout><TeamManagement/></QRLayout></ProtectedWithTrial>} />
        <Route path="/qr/dash/ai-ideas" element={<ProtectedWithTrial><QRLayout><AIIdeas/></QRLayout></ProtectedWithTrial>} />
        <Route path="/qr/dash/debug-stripe" element={<Protected><QRLayout><DebugStripe/></QRLayout></Protected>} />
        <Route path="/qr/debug-stripe" element={<Protected><QRLayout><DebugStripe/></QRLayout></Protected>} />
        <Route path="/qr/team/accept-invite" element={<TeamInviteAccept />} />
        
        {/* Legacy routes - redirect to content */}
        <Route path="/qr/dash/books" element={<Navigate to="/qr/dash/content" replace />} />
        <Route path="/qr/dash/campaigns" element={<Navigate to="/qr/dash/content" replace />} />
        
        {/* New specialized pages for author/publisher features */}
        <Route path="/multishop/:slug" element={<MultiShopPage />} />
        <Route path="/author/:slug" element={<AuthorProfilePage />} />
        <Route path="/sample/:slug" element={<BookSamplePage />} />
        <Route path="/sample/:accountSlug/:slug" element={<BookSamplePage />} />
        <Route path="/spotify/:slug" element={<SpotifyPage />} />
        <Route path="/spotify/:accountSlug/:slug" element={<SpotifyPage />} />

        {/* Public pricing page */}
        <Route path="/pricing" element={
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <PricingAuth />
            </main>
            <Footer />
            <CookieBanner />
          </div>
        } />

        {/* Authenticated pricing page */}
        <Route path="/qr/pricing" element={
          <Protected><PricingAuth /></Protected>
        } />
      </Routes>
    </AuthRedirect>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    <Router>
      <AppContent />
    </Router>
    </ErrorBoundary>
  );
}

export default App;
